/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  useCallback,
  useEffect,
  useState
} from 'react';
import {makeStyles, TextField} from '@material-ui/core';
import debounce from 'lodash.debounce';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {UsePaginationResult} from '@ion/query/src/hooks/usePagination';
import Tooltip from '../components/tooltip/Tooltip';
import {useStateRef} from '../hooks/useStateRef';
import {dlsColor} from '../theme/theme';
import {resetValue} from '../utils/ComponentUtils';
import {getPageProxy} from './IonGridUtils';
import usePageConstraints, {hasConstraintExceeded} from './usePageConstraints';

export interface PageConstraintsProps {
  constraint?: number;
  entity?: string;
};

// defines the props definitions for GoToPage component...
export interface GoToPageProps {
  gridApi: GenericObject,
  paging?: UsePaginationResult,
  startRow: number,
  pageSize: number,
  totalPage: number,
  totalResultCount: number,
  loading?: boolean,
  pageConstraintProps?: PageConstraintsProps,
  onManualPaginationChange?: Function
};

// defines the style definitions for GoToPage component...
const useStyles = makeStyles((theme) => ({
  root : {
    display        : 'inline-flex',
    justifyContent : 'flex-end',
    alignItems     : 'center'
  },
  input : {
    width       : '40px',
    height      : '18px',
    border      : `1px solid ${dlsColor('grey4')}`,
    marginRight : '4px'
  },
  textRoot : {
    height         : '100%',
    justifyContent : 'center'
  },
  underline : {
    padding     : '0 !important',
    margin      : '0 !important',
    fontSize    : '14px !important',
    '& > input' : {
      padding   : '0 !important',
      textAlign : 'center'
    },
    '&&&:before' : {
      border : 'none'
    },
    '&&:after' : {
      border : 'none'
    }
  }
}));

// private function to calculate the current page by using start index of the grid and page size
const getPage = (start: number, size: number) => Math.floor(start / size) + 1;

/**
 * Defining the GoToPage component. This component allows the user to Jump to the
 * available pages in the grid within the text field.
 *
 * ```typescript
 *
 *  import {GoToPage} from '@ion/dls/src/grid/';
 *
 *  <GoToPage gridApi{api}
 *    startRow={startIndex}
 *    pageSize={size}
 *    totalPage={totalPage} />
 *
 * ```
 */
export const GoToPage = memo(({
  gridApi,
  paging,
  startRow,
  pageSize,
  totalPage,
  totalResultCount,
  pageConstraintProps,
  onManualPaginationChange,
  loading = false
}: GoToPageProps) => {
  const classes                                     = useStyles();
  const [constraintExceeded, setConstraintExceeded] = useState(false);
  const [
    currentPage,
    setCurrentPage,
    currentPageRef
  ] = useStateRef(getPage(startRow, pageSize));

  const isConstraintExceeded = usePageConstraints({
    pageConstraint : pageConstraintProps?.constraint,
    page           : currentPageRef.current,
    pageSize
  });

  useEffect(() => {
    setCurrentPage(getPage(startRow, pageSize));
  }, [startRow, pageSize]);

  const goToPage = useCallback(debounce(() => {
    const page          = currentPageRef.current - 1;
    const proxy         = getPageProxy(gridApi, totalResultCount);
    const isNotSamePage = paging ? (paging?.page !== currentPageRef.current) : (proxy.currentPage !== page);
    const hasConstraint = hasConstraintExceeded(
      currentPageRef.current,
      pageSize,
      pageConstraintProps?.constraint
    );
    if (Number.isInteger(page) && isNotSamePage && !hasConstraint) {
      gridApi.paginationGoToPage(page);
      onManualPaginationChange?.();
      paging?.gotoPage(page + 1);
      setConstraintExceeded(false);
    } else if (hasConstraint) {
      setCurrentPage(getPage(startRow, pageSize));
      resetValue({
        fnCall       : setConstraintExceeded,
        initialValue : true,
        resetValue   : false,
        delay        : 3000
      });
    }
  }, 1500), [gridApi, paging, pageSize, pageConstraintProps?.constraint, startRow]);

  const onSetPage = useCallback(({target: {value}}) => {
    const page = parseInt(value);
    if (isNaN(page) || (page > 0 && page <= totalPage)) {
      setCurrentPage(page);
      goToPage();
    }
  }, [totalPage, goToPage]);

  const onBlur = useCallback(() => {
    if (!currentPageRef.current) {
      setCurrentPage(getPage(startRow, pageSize));
    }
  }, [startRow, pageSize]);

  return (
    <div className={classes.root}>
      <div className={classes.input}>
        <Tooltip title={translate('grids:pagination.constraint', {
          pageConstraint : pageConstraintProps?.constraint,
          entity         : pageConstraintProps?.entity
        })}
        isOpen={constraintExceeded}
        renderOnlyChildren={!constraintExceeded && !isConstraintExceeded}>
          <TextField label=''
            type='number'
            disabled={loading}
            value={currentPage}
            onChange={onSetPage}
            onBlur={onBlur}
            classes={{root: classes.textRoot}}
            InputProps={{classes: {underline: classes.underline}}} />
        </Tooltip>
      </div>
      / {totalPage || '-'}
    </div>
  );
});

export default GoToPage;
